import React, { useState, useEffect } from 'react';
import { 
  Search, 
  X
} from 'lucide-react';
import { ExecutionDocument } from '../../../../../shared/services/documentService';
import { DocumentsByKnowledgeBase } from './DocumentsByKnowledgeBase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface DocumentSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  knowledgeBaseDocuments: ExecutionDocument[];
  isLoading: boolean;
  onDocumentSelect: (doc: ExecutionDocument, isCategory?: boolean) => void;
}

/**
 * Function to organize documents by Knowledge Base
 */
function organizeByKnowledgeBase(docs: ExecutionDocument[]) {
  const organized: Record<string, {
    id: string;
    name: string;
    documents: ExecutionDocument[];
  }> = {};
  
  docs.forEach(doc => {
    const kbName = doc.knowledge_base_name || 'Unclassified';
    const kbId = doc.metadata?.knowledge_base_id || 'unclassified';
    
    if (!organized[kbId]) {
      organized[kbId] = {
        id: kbId,
        name: kbName,
        documents: []
      };
    }
    
    organized[kbId].documents.push(doc);
  });
  
  return Object.values(organized);
}

/**
 * Function to filter documents by search term
 */
function filterDocuments(
  kbList: Array<{ id: string; name: string; documents: ExecutionDocument[] }>,
  searchTerm: string
) {
  if (!searchTerm.trim()) {
    return kbList;
  }
  
  const lowerSearch = searchTerm.toLowerCase();
  
  return kbList
    .map(kb => ({
      ...kb,
      documents: kb.documents.filter(doc => 
        doc.name.toLowerCase().includes(lowerSearch) ||
        doc.category?.toLowerCase().includes(lowerSearch)
      )
    }))
    .filter(kb => kb.documents.length > 0);
}

export const DocumentSearchModal: React.FC<DocumentSearchModalProps> = ({
  isOpen,
  onClose,
  knowledgeBaseDocuments,
  isLoading,
  onDocumentSelect
}) => {
  const [searchValue, setSearchValue] = useState('');
  
  // Reset search cuando se cierra
  useEffect(() => {
    if (!isOpen) {
      setSearchValue('');
    }
  }, [isOpen]);
  
  // Organizar y filtrar documentos
  const organized = organizeByKnowledgeBase(knowledgeBaseDocuments);
  const filtered = filterDocuments(organized, searchValue);
  
  const handleDocumentSelect = (doc: ExecutionDocument, isCategory?: boolean) => {
    onDocumentSelect(doc, isCategory);
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[500px] h-[80vh] flex flex-col gap-0 p-0">
        <DialogHeader className="p-4 border-b">
          <div className="flex items-center justify-between mb-2">
            <DialogTitle>Select Documents</DialogTitle>
          </div>
          
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search by name or category..."
              className="pl-9 pr-8"
            />
            {searchValue && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1 h-7 w-7"
                onClick={() => setSearchValue('')}
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </DialogHeader>
        
        {/* Lista de documentos organizados */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
              <span className="loading-spinner" /> {/* Assuming you have a spinner or use text */}
              <div className="text-sm">Loading documents...</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8 text-center">
              {searchValue ? (
                <>
                  <div className="font-medium mb-1">No results found</div>
                  <div className="text-sm">Try a different search term</div>
                </>
              ) : (
                <>
                  <div className="font-medium mb-1">No documents available</div>
                  <div className="text-sm">No documents found in knowledge bases</div>
                </>
              )}
            </div>
          ) : (
            <DocumentsByKnowledgeBase 
              data={filtered}
              onSelect={handleDocumentSelect}
            />
          )}
        </div>
        
        {/* Footer con info */}
        {!isLoading && filtered.length > 0 && (
          <DialogFooter className="p-3 border-t bg-muted/20 sm:justify-start">
            <div className="text-xs text-muted-foreground w-full text-center sm:text-left">
              {filtered.length} knowledge base{filtered.length !== 1 ? 's' : ''} • {' '}
              {filtered.reduce((sum, kb) => sum + kb.documents.length, 0)} document{filtered.reduce((sum, kb) => sum + kb.documents.length, 0) !== 1 ? 's' : ''}
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
